// src/pages/ConfirmedPairsPage.js
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FeedbackDetailsModal from '../components/FeedbackDetailsModal';
import { useTranslation } from 'react-i18next';
import { mentoringApi as mApi, surveyApi } from '../axiosInstance';

const LS_KEY = 'hr_overview_project_filter_v1';

function readSavedProjectFilterId() {
  const raw = localStorage.getItem(LS_KEY);
  if (!raw) return '';

  try {
    const v = JSON.parse(raw);
    const n = Number(v?.surveyId ?? v?.id ?? v);
    // Schutz gegen Number(null) => 0 / Number('') => 0
    if (!Number.isFinite(n) || n <= 0) return '';
    return String(n);
  } catch {
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return '';
    return String(n);
  }
}

function writeSavedProjectFilterId(id) {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) {
      try {
        const obj = JSON.parse(raw);
        if (obj && typeof obj === 'object') {
          obj.surveyId = Number(id);
          localStorage.setItem(LS_KEY, JSON.stringify(obj));
          return;
        }
      } catch {
        // ignore, fall back
      }
    }
    localStorage.setItem(LS_KEY, String(id));
  } catch {
    // ignore
  }
}

function clearSavedProjectFilterId() {
  try {
    localStorage.removeItem(LS_KEY);
  } catch {
    // ignore
  }
}

export default function ConfirmedPairsPage() {
  const [pairs, setPairs] = useState([]);
  const [selectedPair, setSelectedPair] = useState(null);

  // ✅ Neu: Projekte (Surveys) unabhängig von pairs laden → Dropdown zeigt immer alle Projekte
  const [projects, setProjects] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlSurveyId = searchParams.get('surveyId') || searchParams.get('survey_id') || '';

  // ✅ GLOBAL-LOCK: Nur wenn HrOverview einen konkreten Survey gesetzt hat
  const [overviewSurveyId] = useState(() => readSavedProjectFilterId());
  const isOverviewLocked = Boolean(overviewSurveyId);

  // ✅ initial: URL gewinnt, sonst globaler Overview-Filter
  const [projectFilter, setProjectFilter] = useState(() => urlSurveyId || overviewSurveyId);

  const navigate = useNavigate();
  const { t } = useTranslation();

  // ✅ Projekte (Surveys) laden (wie HrOverview) → Dropdown-Optionen
  useEffect(() => {
    let alive = true;

    async function fetchProjects() {
      try {
        // Erwartet typischerweise: [{ id, title }] oder [{ survey_id, title }]
        const res = await surveyApi.get('/surveys', { withCredentials: true });

        const list = Array.isArray(res.data) ? res.data : [];
        const normalized = list
          .map((s) => ({
            id: s.survey_id ?? s.id ?? s.surveyId ?? null,
            title: s.title ?? s.survey_title ?? s.name ?? null,
          }))
          .filter((x) => x.id !== null && x.id !== undefined);

        if (!alive) return;
        setProjects(normalized);
      } catch (err) {
        // Fallback: nichts – Dropdown wird dann aus pairs abgeleitet (wenn möglich)
        console.error('Fehler beim Laden der Projekte (surveys):', err);
        if (!alive) return;
        setProjects([]);
      }
    }

    fetchProjects();
    return () => {
      alive = false;
    };
  }, []);

  // ✅ Sync: Wenn URL sich ändert → State nachziehen.
  // Im Lock-Modus immer Overview-Filter erzwingen.
  useEffect(() => {
    if (isOverviewLocked) {
      setProjectFilter(String(overviewSurveyId));
      return;
    }
    setProjectFilter(urlSurveyId);
  }, [urlSurveyId, isOverviewLocked, overviewSurveyId]);

  // ✅ Pairs laden: immer serverseitig filtern (wenn surveyId gesetzt ist)
  useEffect(() => {
    let alive = true;

    async function fetchPairs() {
      try {
        const params = {};
        if (projectFilter) params.surveyId = projectFilter;

        const res = await mApi.get('/confirmed-pairs', { params });

        if (!alive) return;
        setPairs(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Fehler beim Laden der bestätigten Paare:', err);
        if (!alive) return;
        setPairs([]);
      }
    }

    fetchPairs();
    return () => {
      alive = false;
    };
  }, [projectFilter]);

  // Fallback (falls projects nicht geladen werden kann): Projekte aus pairs ableiten
  const uniqueProjectsFromPairs = useMemo(() => {
    return Array.from(
      new Map(
        pairs
          .filter((p) => p.survey_id)
          .map((p) => [String(p.survey_id), p.survey_title])
      )
    );
  }, [pairs]);

  // ✅ Dropdown-Optionen: bevorzugt echte Projektliste; Fallback auf pairs
  const projectOptions = useMemo(() => {
    const base = projects.length
      ? projects.map((p) => [String(p.id), p.title])
      : uniqueProjectsFromPairs;

    // dedupe nach id
    const map = new Map();
    for (const [id, title] of base) {
      if (!map.has(String(id))) map.set(String(id), title ?? null);
    }

    const opts = Array.from(map.entries());

    // Wenn ein Filter gesetzt ist, aber nicht vorkommt → trotzdem anzeigen
    const f = projectFilter ? String(projectFilter) : '';
    if (f && !opts.some(([id]) => String(id) === f)) {
      opts.unshift([f, null]);
    }

    return opts;
  }, [projects, uniqueProjectsFromPairs, projectFilter]);

  // ✅ Backend filtert bereits (wenn surveyId gesetzt ist)
  const filteredPairs = pairs;

  function onChangeProjectFilter(value) {
    const v = value || '';

    // ✅ Lock-Modus: kein Wechsel möglich
    if (isOverviewLocked) {
      const enforced = String(overviewSurveyId);
      setProjectFilter(enforced);

      const next = new URLSearchParams(searchParams);
      next.set('surveyId', enforced);
      next.delete('survey_id');

      setSearchParams(next, { replace: true });
      return;
    }

    setProjectFilter(v);

    if (v) writeSavedProjectFilterId(v);
    else clearSavedProjectFilterId();

    const next = new URLSearchParams(searchParams);
    if (v) next.set('surveyId', String(v));
    else next.delete('surveyId');

    next.delete('survey_id');
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="min-h-screen bg-black text-white p-6 space-y-6">
      <button
        onClick={() => navigate('/hr/overview')}
        className="bg-green-400 text-black font-semibold px-4 py-2 rounded-full hover:bg-green-300 transition"
      >
        ← {t('backToHRDashboard')}
      </button>

      <h1 className="text-3xl font-bold">{t('confirmedMentoringPairs')}</h1>

      {/* Filter nach Projekt-ID */}
      <div className="mb-6 flex gap-4 items-center">
        <label htmlFor="project-filter" className="text-sm text-gray-200">
          {t('projectFilter')}:
        </label>

        <select
          id="project-filter"
          value={projectFilter}
          onChange={(e) => onChangeProjectFilter(e.target.value)}
          disabled={isOverviewLocked}
          className={
            'p-2 rounded bg-gray-800 text-white ' +
            (isOverviewLocked ? 'opacity-70 cursor-not-allowed' : '')
          }
          title={
            isOverviewLocked
              ? (t('projectFilterLocked', 'Projektfilter ist durch HR-Übersicht fixiert.') || '')
              : ''
          }
        >
          {/* "Alle Projekte" nur wenn NICHT gelockt */}
          {!isOverviewLocked && <option value="">{t('allProjects')}</option>}

          {projectOptions.map(([id, title]) => (
            <option key={String(id)} value={String(id)}>
              {title ? `${t('project')}: ${title}` : `${t('projectId')}: ${id}`}
            </option>
          ))}
        </select>

        {isOverviewLocked && (
          <div className="text-xs text-gray-400">
            {t('projectFilterLockedHint', 'Filter wird aus der HR-Übersicht übernommen.')}
          </div>
        )}
      </div>

      {filteredPairs.length === 0 ? (
        <div className="bg-gray-800 rounded-2xl p-6 text-gray-300">
          {t('noData', 'Keine bestätigten Paare für die aktuelle Auswahl.')}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPairs.map((pair, index) => {
            const stableKey =
              pair.matching_id ??
              pair.id ??
              pair.agreement_id ??
              `${pair.mentee_user_id || ''}-${pair.mentor_user_id || ''}-${pair.survey_id || ''}-${index}`;

            return (
              <div
                key={stableKey}
                className={
                  'bg-gray-800 p-4 rounded-xl shadow-md space-y-4 cursor-pointer hover:bg-gray-700 transition border ' +
                  (pair.status === 'rejected'
                    ? 'shadow-lg shadow-red-700/70 border-red-700'
                    : 'border-transparent')
                }
                onClick={() => setSelectedPair(pair)}
              >
                <div className="flex items-center gap-4">
                  <img
                    src={`/mentoring/uploads/${pair.mentee_avatar_path}`}
                    alt="Mentee"
                    className="w-12 h-12 rounded-full object-cover border border-gray-600"
                  />
                  <div>
                    <div className="font-semibold text-white">
                      {pair.mentee_first_name} {pair.mentee_last_name}
                    </div>
                    <div className="text-sm text-gray-400">
                      {pair.mentee_abteilung} · {pair.mentee_job_title}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <img
                    src={`/mentoring/uploads/${pair.mentor_avatar_path}`}
                    alt="Mentor"
                    className="w-12 h-12 rounded-full object-cover border border-gray-600"
                  />
                  <div>
                    <div className="font-semibold text-white">
                      {pair.mentor_first_name} {pair.mentor_last_name}
                    </div>
                    <div className="text-sm text-gray-400">
                      {pair.mentor_abteilung} · {pair.mentor_job_title}
                    </div>
                  </div>
                </div>

                {(pair.survey_title || pair.survey_id) && (
                  <div className="text-xs text-gray-400 mt-2">
                    {pair.survey_title
                      ? `${t('project')}: ${pair.survey_title}`
                      : pair.survey_id
                      ? `${t('projectId')}: ${pair.survey_id}`
                      : ''}
                  </div>
                )}

                {pair.total_score !== null && pair.total_score !== undefined && (
                  <div className="mt-2 text-sm text-white font-semibold bg-gray-700 p-2 rounded text-center">
                    {t('satisfaction')}: {pair.total_score} / 5
                  </div>
                )}

                {pair.status === 'rejected' && (
                  <div className="text-xs text-red-400 font-semibold mt-2">
                    {t('status')}: {t('rejected')}
                  </div>
                )}
                {pair.status === 'confirmed' && (
                  <div className="text-xs text-green-400 font-semibold mt-2">
                    {t('status')}: {t('confirmed')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedPair && (
        <FeedbackDetailsModal pair={selectedPair} onClose={() => setSelectedPair(null)} />
      )}
    </div>
  );
}
